import React, { useEffect, useRef } from 'react';
import { ConstructorEventUpdateSQLStatus } from "../../../Event/event";

const BuildNumberGame = () => {
  const containerRef = useRef(null);
  const slotsRef = useRef([]);
  const messageRef = useRef(null);
  const targetNumberRef = useRef(null);
  const resetBtnRef = useRef(null);
  const digitBlocks = useRef([]);
  const numbersToBuild = [473, 628, 195, 307, 582, 749, 901, 864, 730, 417, 389, 651, 274, 830, 159];

  let targetNumber;
  let targetDigits = [];
  let dragged = null;
  let offsetX = 0, offsetY = 0;

  const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  const createDigitBlocks = () => {
    digitBlocks.current.forEach(block => block.remove());
    digitBlocks.current = [];

    let digitsPool = [...targetDigits];
    while (digitsPool.length < 15) {
      digitsPool.push(randInt(0, 9).toString());
    }
    digitsPool = digitsPool.sort(() => Math.random() - 0.5);

    digitsPool.forEach(digit => {
      const block = document.createElement('div');
      Object.assign(block.style, {
        position: 'absolute',
        width: 'clamp(40px, 12vw, 60px)',
        height: 'clamp(60px, 18vw, 80px)',
        background: '#ff80ab',
        borderRadius: '15px',
        boxShadow: '0 8px 20px rgba(255, 64, 129, 0.5)',
        fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
        color: '#880e4f',
        fontWeight: '900',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'grab',
        userSelect: 'none',
        zIndex: 1
      });
      block.className = 'digit-block';
      block.textContent = digit;
      block.dataset.value = digit;
      containerRef.current.appendChild(block);

      let x, y;
      do {
        x = randInt(10, window.innerWidth - 70);
        y = randInt(10, window.innerHeight - 100);
      } while (
        x > window.innerWidth / 2 - 190 && x < window.innerWidth / 2 + 190 &&
        y > window.innerHeight / 2 - 130 && y < window.innerHeight / 2 + 130
      );

      block.style.left = x + 'px';
      block.style.top = y + 'px';

      block.addEventListener('mousedown', onDragStart);
      block.addEventListener('touchstart', onDragStart, { passive: false });

      digitBlocks.current.push(block);
    });
  };

  const onDragStart = (e) => {
    e.preventDefault();
    dragged = e.currentTarget;
    dragged.style.transition = 'none';

    if (e.type === 'mousedown') {
      offsetX = e.clientX - dragged.offsetLeft;
      offsetY = e.clientY - dragged.offsetTop;
      document.addEventListener('mousemove', onDragMove);
      document.addEventListener('mouseup', onDragEnd);
    } else {
      const touch = e.touches[0];
      offsetX = touch.clientX - dragged.offsetLeft;
      offsetY = touch.clientY - dragged.offsetTop;
      document.addEventListener('touchmove', onDragMove, { passive: false });
      document.addEventListener('touchend', onDragEnd);
    }

    dragged.style.zIndex = 9999;
  };

  const onDragMove = (e) => {
    e.preventDefault();
    let clientX, clientY;
    if (e.type === 'mousemove') {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    }

    let x = clientX - offsetX;
    let y = clientY - offsetY;
    x = Math.min(Math.max(0, x), window.innerWidth - dragged.offsetWidth);
    y = Math.min(Math.max(0, y), window.innerHeight - dragged.offsetHeight);
    dragged.style.left = x + 'px';
    dragged.style.top = y + 'px';
  };

  const onDragEnd = (e) => {
    e.preventDefault();
    const rects = slotsRef.current.map(slot => slot.getBoundingClientRect());
    const draggedRect = dragged.getBoundingClientRect();
    let droppedOnSlot = null;

    rects.forEach((rect, i) => {
      if (
        draggedRect.left + draggedRect.width / 2 > rect.left &&
        draggedRect.left + draggedRect.width / 2 < rect.right &&
        draggedRect.top + draggedRect.height / 2 > rect.top &&
        draggedRect.top + draggedRect.height / 2 < rect.bottom
      ) {
        droppedOnSlot = slotsRef.current[i];
      }
    });

    if (droppedOnSlot) {
      placeDigitInSlot(dragged, droppedOnSlot);
    }

    document.removeEventListener('mousemove', onDragMove);
    document.removeEventListener('mouseup', onDragEnd);
    document.removeEventListener('touchmove', onDragMove);
    document.removeEventListener('touchend', onDragEnd);

    if (dragged) {
      dragged.style.zIndex = 1;
      dragged = null;
    }
  };

  const placeDigitInSlot = (digitBlock, slot) => {
    const digitValue = digitBlock.dataset.value;
    if (slot.dataset.digit) {
      returnDigitToPool(slot.dataset.digit);
    }
    slot.textContent = digitValue;
    slot.style.background = '#ce93d8';
    slot.style.color = '#4a148c';
    slot.dataset.digit = digitValue;
    digitBlock.remove();
    checkIfComplete();
  };

  const returnDigitToPool = (digit) => {
    const block = document.createElement('div');
    block.className = 'digit-block';
    block.textContent = digit;
    block.dataset.value = digit;
    Object.assign(block.style, {
      position: 'absolute',
      width: 'clamp(40px, 12vw, 60px)',
      height: 'clamp(60px, 18vw, 80px)',
      background: '#ff80ab',
      borderRadius: '15px',
      boxShadow: '0 8px 20px rgba(255, 64, 129, 0.5)',
      fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
      color: '#880e4f',
      fontWeight: '900',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'grab',
      userSelect: 'none',
      zIndex: 1
    });
    containerRef.current.appendChild(block);

    let x, y;
    do {
      x = randInt(10, window.innerWidth - 70);
      y = randInt(10, window.innerHeight - 100);
    } while (
      x > window.innerWidth / 2 - 190 && x < window.innerWidth / 2 + 190 &&
      y > window.innerHeight / 2 - 130 && y < window.innerHeight / 2 + 130
    );

    block.style.left = x + 'px';
    block.style.top = y + 'px';
    block.addEventListener('mousedown', onDragStart);
    block.addEventListener('touchstart', onDragStart, { passive: false });
    digitBlocks.current.push(block);
  };

  const checkIfComplete = () => {
    const digitsInSlots = slotsRef.current.map(s => s.dataset.digit || '');
    if (digitsInSlots.includes('')) {
      messageRef.current.textContent = '';
      return;
    }
    const builtNumber = digitsInSlots.join('');
    if (builtNumber === targetNumber.toString()) {
      messageRef.current.style.color = '#388e3c';
      messageRef.current.textContent = '🎉 Awesome! You built the number!';
      setTimeout(() => {
        setupNewGame();
        const responsedata = ConstructorEventUpdateSQLStatus();
      console.log("Event completed", responsedata);
      }, 400000);
    } else {
      messageRef.current.style.color = '#b71c1c';
      messageRef.current.textContent = '❌ Oops, try again!';
    }
  };

  const setupNewGame = () => {
    slotsRef.current.forEach(slot => {
      slot.textContent = '?';
      slot.style.background = '#f3e5f5';
      slot.style.color = '#d1c4e9';
      delete slot.dataset.digit;
    });
    messageRef.current.textContent = '';
    targetNumber = numbersToBuild[randInt(0, numbersToBuild.length - 1)];
    targetDigits = targetNumber.toString().split('');
    targetNumberRef.current.textContent = targetNumber;
    createDigitBlocks();
  };

  useEffect(() => {
    setupNewGame();
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        height: '100vh', width: '100vw', overflow: 'hidden',
        position: 'relative', background: 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)',
        fontFamily: 'Fredoka, sans-serif'
      }}
    >
      <div
        style={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: '#fff', borderRadius: '20px', boxShadow: '0 12px 35px rgba(0,0,0,0.2)',
          padding: 'clamp(1rem, 4vw, 2rem)', width: 'clamp(300px, 80vw, 380px)', display: 'flex', flexDirection: 'column', alignItems: 'center'
        }}
      >
        <div ref={targetNumberRef} style={{
          fontSize: 'clamp(2rem, 6vw, 3.5rem)', color: '#7b1fa2', marginBottom: '25px',
          letterSpacing: '5px', textShadow: '1px 1px 3px #ab47bc'
        }}>000</div>

        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          {['100s', '10s', '1s'].map((label, index) => (
            <div key={label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
              <div
                ref={el => slotsRef.current[index] = el}
                data-place={label}
                style={{
                  height: 'clamp(80px, 15vw, 100px)', width: 'clamp(60px, 20vw, 80px)', background: '#f3e5f5',
                  borderRadius: '20px', boxShadow: 'inset 0 0 15px #ce93d8', fontSize: '3rem',
                  fontWeight: '900', color: '#d1c4e9', display: 'flex', justifyContent: 'center',
                  alignItems: 'center', cursor: 'pointer', marginBottom: '8px'
                }}
                onClick={() => {
                  const slot = slotsRef.current[index];
                  if (slot.dataset.digit) {
                    returnDigitToPool(slot.dataset.digit);
                    slot.textContent = '?';
                    slot.style.background = '#f3e5f5';
                    slot.style.color = '#d1c4e9';
                    delete slot.dataset.digit;
                    messageRef.current.textContent = '';
                  }
                }}
              >?</div>
              <div style={{ fontWeight: 'bold', color: '#6a1b9a', fontSize: '1rem' }}>{label}</div>
            </div>
          ))}
        </div>

        <div ref={messageRef} style={{
          marginTop: '20px', fontSize: '1.4rem', fontWeight: 700,
          minHeight: '30px', textAlign: 'center', color: '#4a148c'
        }}></div>

        <button
          ref={resetBtnRef}
          onClick={setupNewGame}
          style={{
            marginTop: '20px', background: '#7b1fa2', border: 'none', borderRadius: '30px',
            padding: '12px 35px', color: 'white', fontWeight: 700, fontSize: '1.2rem',
            cursor: 'pointer', boxShadow: '0 8px 20px rgba(123, 31, 162, 0.5)'
          }}
        >
          New Number
        </button>
      </div>
    </div>
  );
};

export default BuildNumberGame;
